<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import type { WorkspaceMarkerField } from '~/codegen/graphql'
import { SearchResultType } from '~/codegen/graphql'

const props = defineProps<{
  type: SearchResultType
  id?: string | null
  label?: string | null
  chainCount?: number | null
  teamspaceId?: string | null
  teamspaceLabel?: string | null
  teamspaceIcon?: WorkspaceMarkerField | null
  searchedTerm?: string
}>()

defineEmits<{
  openHistory: [id: string, label: string, type: SearchResultType, teamspaceId: string | null | undefined]
}>()

const isTeamspace = computed(() => props.type === SearchResultType.Workspace)

const icon = computed(() => ({
  [SearchResultType.Review]: 'light:file',
  [SearchResultType.Survey]: 'light:clipboard-check',
  [SearchResultType.Dashboard]: 'light:square-poll-vertical',
  [SearchResultType.Spec]: 'light:folder-gear',
  [SearchResultType.Session]: 'light:square-kanban',
  [SearchResultType.Skill]: 'light:book',
  [SearchResultType.Workspace]: null,
})[props.type])

const link = computed((): RouteLocationRaw | undefined => {
  if (props.type === SearchResultType.Workspace && props.teamspaceId) {
    return {
      name: 'teamspace-workspaceId' as const,
      params: { workspaceId: props.teamspaceId },
    }
  }
  else if (props.type === SearchResultType.Skill && props.id) {
    // Skills may not have a workspace, link directly to skill page
    return {
      name: 'skills-hash',
      params: { hash: props.id },
    }
  }
  else if (props.id && props.teamspaceId) {
    const type = searchResultTypeToReviewType(props.type as Exclude<SearchResultType, SearchResultType.Workspace>)
    return getRouteForArtifact(props.id, props.teamspaceId, type)
  }

  return undefined
})

function highlightSearchTerm(text: string, term?: string) {
  if (!term)
    return text
  return text.replace(
    new RegExp(`(${term})`, 'gi'),
    '<span class="bg-yellow-500/50">$1</span>',
  )
}
</script>

<template>
  <NuxtLink
    :to="link"
    data-action="search-result"
    class="group/link flex items-center gap-2 rounded-md p-2 text-base hover:bg-gray-100 focus:bg-gray-100 dark:hover:bg-gray-800 dark:focus:bg-gray-800"
  >
    <UIcon v-if="icon && !isTeamspace" :name="icon" class="size-4 text-gray-500 dark:text-gray-400" />
    <WorkspaceIcon v-else-if="teamspaceIcon" :marker="teamspaceIcon" />

    <div class="flex-1 truncate">
      <span v-html="highlightSearchTerm(props.label || teamspaceLabel || '', searchedTerm)" />
    </div>
    <div v-if="!isTeamspace && teamspaceId" class="ml-3 flex items-center gap-2 opacity-40 group-hover/link:opacity-100 group-focus/link:opacity-100">
      <div class="flex max-w-60 items-center gap-1 truncate text-sm">
        <WorkspaceIcon v-if="teamspaceIcon" :marker="teamspaceIcon" size="sm" />
        <div class="truncate">
          {{ teamspaceLabel }}
        </div>
      </div>
      <UTooltip v-if="chainCount && chainCount > 1 && id && label" text="Show history" class="shrink-0">
        <UChip size="md" color="gray" :text="chainCount - 1">
          <UButton
            size="xs"
            color="gray"
            icon="light:rectangle-history"
            class="-my-0.5"
            @click.prevent.stop="$emit('openHistory', id, label, type, teamspaceId)"
          />
        </UChip>
      </UTooltip>
    </div>
  </NuxtLink>
</template>
